import React, { useState } from "react";
import { useParams } from "react-router";
import { CalWarning } from "../../calcheck/CalWarning";
import { COMMONS } from "../../calendar/commons";
import {
    Common,
    getMorningAndEveningCelebrations,
} from "../../calendar/ordinariate-calendar";
import { getCalendarDay } from "../../calendar/utils";
import { LiturgyViewer } from "../../liturgy/LiturgyViewer";
import { RUBRIC_COLOR } from "../../tokens";
import { useDataDependency } from "../../utils/RemoteDataProvider";
import { TweakProvider } from "../../utils/TweakProvider";
import { useSettings } from "../../utils/settings";
import { useTrackPray } from "../../utils/tracking";
import {
    OrdinariateLessons,
    getOrdinariateLiturgy,
} from "./ordinariate-liturgy";

export const OrdinariatePage: React.FC<{}> = () => {
    const {
        year,
        month,
        date,
        label,
        hour: rawHour,
    } = useParams<{
        year: string;
        month: string;
        date: string;
        label: string;
        hour: "mattins" | "evensong" | "first-evensong" | "second-evensong";
    }>();

    const hour = rawHour === "evensong" ? "second-evensong" : rawHour;

    const day = getCalendarDay(year, month, date, false);

    const { settings, setSetting } = useSettings();

    useTrackPray(day, "mattins");

    const regionLabels =
        localStorage.getItem("region-switches")?.split(";;;") ?? [];

    const celebrationYear = getMorningAndEveningCelebrations(
        day.year,
        settings.transferEpiphany === "yes",
        settings.transferAscension === "yes",
        settings.transferCorpusChristi === "yes",
        regionLabels
    );

    const celebrationDay = celebrationYear.find(
        (d) =>
            d.date === day.date && d.month === day.month && day.year === d.year
    );

    const celebrationDayIndex = celebrationYear.findIndex(
        (d) =>
            d.date === day.date && d.month === day.month && day.year === d.year
    );

    const easterDayIndex = celebrationYear.findIndex(
        (d) =>
            d.morningOptions.find((cel) => cel.title === "Easter Day") !==
                undefined && day.year === d.year
    );

    const isBeforeEaster = celebrationDayIndex < easterDayIndex;

    const celebrationList =
        hour === "mattins"
            ? celebrationDay?.morningOptions.concat(
                  celebrationDay.morningOutranked
              )
            : celebrationDay?.eveningOptions.concat(
                  celebrationDay.eveningOutranked
              );

    const backupYear = getMorningAndEveningCelebrations(
        day.year,
        settings.transferEpiphany !== "yes",
        settings.transferAscension !== "yes",
        settings.transferCorpusChristi !== "yes",
        regionLabels
    );

    const backupDay = backupYear.find(
        (d) =>
            d.date === day.date && d.month === day.month && day.year === d.year
    );

    const backupList =
        hour === "mattins"
            ? backupDay?.morningOptions.concat(backupDay.morningOutranked)
            : backupDay?.eveningOptions.concat(backupDay.eveningOutranked);

    let celebration = celebrationList
        ?.concat(backupList ?? [])
        .find((c) => c.label === label);

    if (celebration?.propersOfTheFeria) {
        const feriaToday = celebrationList?.find((c) => c.type === "feria");

        celebration = { ...feriaToday, ...celebration };
    }

    const isLent =
        celebrationList?.find(
            (c) =>
                (c.title.includes("Lent") ||
                    c.title.includes("Ash Wednesday") ||
                    c.title.includes("Passion Sunday") ||
                    c.title.includes("Palm Sunday") ||
                    c.title.includes("Holy Week") ||
                    c.title.includes("Good Friday") ||
                    c.title.includes("Holy Saturday")) &&
                !c.title.includes("Before")
        ) !== undefined;

    const isAdvent =
        celebrationList?.find(
            (c) =>
                (c.title.includes("Advent") || c.title.includes("December")) &&
                !c.title.includes("before")
        ) !== undefined;

    const isChristmasOctave =
        celebrationList?.find(
            (c) =>
                c.title.includes("Octave of Christmas") ||
                c.title === "Mary, the Holy Mother of God"
        ) !== undefined;

    if (!celebration) {
        console.log(celebrationList?.concat(backupList ?? []));
        throw new Error("Could not find celebration");
    }

    const hymnIds: string[] = [];
    const benAntIds: string[] = [];
    const magAntIds: string[] = [];
    const invAntIds: string[] = [];

    const isEastertide =
        celebrationList?.find(
            (celebration) =>
                celebration.title.includes("Easter") ||
                celebration.title.includes("Ascension") ||
                celebration.title === "Rogation Monday" ||
                celebration.title === "Rogation Tuesday" ||
                celebration.title === "Rogation Wednesday" ||
                celebration.title === "Rogation Thursday" ||
                celebration.title === "Rogation Friday" ||
                celebration.title === "Rogation Saturday" ||
                celebration.title.includes("Whit") ||
                celebration.title.includes("Pentecost")
        ) !== undefined && celebration.title !== "Trinity Sunday";

    const commonOptionsForRequest: Common[] = [];

    celebration.commons?.forEach((commonId) => {
        let common = COMMONS[commonId];

        if (isEastertide && common.easterOverrides) {
            common = {
                ...common,
                ...common.easterOverrides,
            };
        }

        commonOptionsForRequest.push(common);
    });

    if (hour === "first-evensong") {
        if (celebration.ep1HymnId) {
            hymnIds.push(celebration.ep1HymnId);
        }

        commonOptionsForRequest.forEach((common) => {
            const commonHymnId = common.ep1HymnId;

            if (commonHymnId) {
                hymnIds.push(commonHymnId);
            }
        });

        if (celebration.ep1MagId) {
            magAntIds.push(celebration.ep1MagId);
        }

        commonOptionsForRequest.forEach((common) => {
            const commonAntId = common.ep1MagId;

            if (commonAntId) {
                magAntIds.push(commonAntId);
            }
        });
    }

    if (hour === "mattins") {
        if (celebration.mpHymnId) {
            hymnIds.push(celebration.mpHymnId);
        }

        commonOptionsForRequest.forEach((common) => {
            const commonHymnId = common.mpHymnId;

            if (commonHymnId) {
                hymnIds.push(commonHymnId);
            }
        });

        if (celebration.mpBenId) {
            benAntIds.push(celebration.mpBenId);
        }

        commonOptionsForRequest.forEach((common) => {
            const commonAntId = common.mpBenId;

            if (commonAntId) {
                benAntIds.push(commonAntId);
            }
        });

        if (celebration.invId) {
            invAntIds.push(celebration.invId);
        }

        commonOptionsForRequest.forEach((common) => {
            const commonAntId = common.invId;

            if (commonAntId) {
                invAntIds.push(commonAntId);
            }
        });
    }

    if (hour === "second-evensong") {
        if (celebration.ep2HymnId) {
            hymnIds.push(celebration.ep2HymnId);
        }

        commonOptionsForRequest.forEach((common) => {
            const commonHymnId = common.ep2HymnId;

            if (commonHymnId) {
                hymnIds.push(commonHymnId);
            }
        });

        if (celebration.ep2MagId) {
            magAntIds.push(celebration.ep2MagId);
        }

        commonOptionsForRequest.forEach((common) => {
            const commonAntId = common.ep2MagId;

            if (commonAntId) {
                magAntIds.push(commonAntId);
            }
        });
    }

    const loadableLessons = useDataDependency<OrdinariateLessons>({
        type: "ord readings",
        day,
        hour,
        celebration: celebration,
        hymnIds,
        benAntIds,
        magAntIds,
        invAntIds,
        oneDayAheadPsalter: settings.oneDayAheadPsalter === "true",
    });

    console.log(loadableLessons);

    const [selectedOtCanticle, setSelectedOtCanticle] = useState("");

    let officeLabel = "Mattins";

    if (hour !== "mattins") {
        if (
            celebration.type === "sunday" ||
            celebration.type === "solemnity" ||
            (celebration.type === "feast" && celebration.feastHasFirstEvensong)
        ) {
            if (hour === "first-evensong") {
                officeLabel = "First Evensong";
            } else {
                officeLabel = "Second Evensong";
            }
        } else {
            officeLabel = "Evensong";
        }
    }

    const [antiphonOption, setAntiphonOption] = useState("Full");

    const [selectedCommon, setSelectedCommon] = useState(
        celebration.commons?.[0] ?? ""
    );

    let celebrationWithCommonSelected = celebration;

    if (selectedCommon !== "" && !celebration.specialManner) {
        celebrationWithCommonSelected = {
            ...COMMONS[selectedCommon],
            ...celebration,
        };
    }

    if (isEastertide && COMMONS[selectedCommon]?.easterOverrides) {
        celebrationWithCommonSelected = {
            ...celebrationWithCommonSelected,
            ...COMMONS[selectedCommon].easterOverrides,
        };
    }

    const [selectedPsalms, setSelectedPsalms] = useState("");

    const [simplifiedHaecDies, onSetSimplifiedHaecDies] = useState("Full");

    const [includeIntroduction, onSetIncludeIntroduction] = useState("Omit");

    const [properVenite, onSetProperVenite] = useState("Proper chant");

    const [resurrectionChant, onSetResurrectionChant] = useState("Text");

    const liturgy = getOrdinariateLiturgy(
        day,
        hour,
        celebrationWithCommonSelected,
        settings,
        loadableLessons,
        selectedOtCanticle,
        setSelectedOtCanticle,
        settings.salutation === "o lord hear our prayer"
            ? "Lay officiant"
            : "Priest officiant",
        (newType: string) => {
            if (newType === "Lay officiant") {
                setSetting("salutation", "o lord hear our prayer");
            }
            if (newType === "Priest officiant") {
                setSetting("salutation", "the lord be with you");
            }
        },
        antiphonOption,
        setAntiphonOption,
        selectedCommon,
        setSelectedCommon,
        selectedPsalms,
        setSelectedPsalms,
        isAdvent,
        isChristmasOctave,
        isLent,
        isEastertide,
        simplifiedHaecDies,
        onSetSimplifiedHaecDies,
        includeIntroduction,
        onSetIncludeIntroduction,
        properVenite,
        onSetProperVenite,
        isBeforeEaster,
        settings.marianAntiphonLanguage ?? "Latin",
        (newMarianAntiphonLanguage: string) => {
            setSetting("marianAntiphonLanguage", newMarianAntiphonLanguage);
        },
        resurrectionChant,
        onSetResurrectionChant
    );

    // useEffect(() => {
    //     const paragraphs =[];

    //     liturgy.forEach(item => {
    //         switch (item.type) {
    //             if (item.type === "heading")
    //         }
    //     })

    //     const doc = new Document({
    //         sections: [
    //             {
    //                 properties: {},
    //                 children: paragraphs, [
    //                     new Paragraph({
    //                         children: [
    //                             new TextRun("Hello World"),
    //                             new TextRun({
    //                                 text: "Foo Bar",
    //                                 bold: true,
    //                                 size: 40,
    //                             }),
    //                             new TextRun({
    //                                 children: [new Tab(), "Github is the best"],
    //                                 bold: true,
    //                             }),
    //                         ],
    //                     }),
    //                 ],
    //             },
    //         ],
    //     });

    //     Packer.toBlob(doc).then((blob) => {
    //         // saveAs from FileSaver will download the file
    //         //saveAs(blob, "example.docx");

    //         var blobUrl = URL.createObjectURL(blob);
    //         console.log(">>", blobUrl);
    //         var link = document.createElement("a"); // Or maybe get it from the current document
    //         link.href = blobUrl;
    //         link.download = "aDefaultFileName.txt";
    //         link.innerHTML = "Click here to download the file";
    //         document.body.appendChild(link);
    //         window.open(blobUrl);
    //     });
    // }, []);

    return (
        <TweakProvider initialState={{}}>
            <div
                style={{
                    textAlign: "center",
                    padding: 24,
                    paddingTop: 64,
                }}
            >
                <div
                    style={{
                        color: RUBRIC_COLOR,
                        fontWeight: "bold",
                        paddingBottom: 8,
                    }}
                >
                    {celebration.regionNote}
                </div>
                <div
                    style={{
                        fontSize: "120%",
                        fontWeight: "bold",
                        lineHeight: 1.2,
                    }}
                >
                    {officeLabel} of
                </div>
                <div
                    style={{
                        fontSize: "150%",
                        fontWeight: "bold",
                        lineHeight: 1.2,
                    }}
                >
                    {celebration.title}
                    {celebration.subtitle ? `, ${celebration.subtitle}` : ""}
                </div>
                {celebration.commonlyCalled ? (
                    <>
                        <div style={{}}>Commonly Called</div>
                        <div style={{ fontSize: "150%", fontWeight: "bold" }}>
                            {celebration.commonlyCalled}
                        </div>
                    </>
                ) : (
                    <></>
                )}
                <div
                    style={{
                        paddingTop: 4,
                        fontWeight: "bold",
                        color: RUBRIC_COLOR,
                    }}
                >
                    {celebration.optional ? "Optional" : ""}{" "}
                    {
                        {
                            "optional memorial": "",
                            memorial: "Memorial",
                            feast: "Feast",
                            solemnity: "Solemnity",
                            sunday: "",
                            misc: "",
                            feria: "Feria",
                        }[celebration.type]
                    }
                    {celebration.optionalInLent ? " (optional in Lent)" : ""}
                </div>
            </div>
            <LiturgyViewer
                items={liturgy}
                label={`${
                    celebration.label
                }-${officeLabel.toLocaleLowerCase()}`}
                superTitle={`${officeLabel} of`}
                title={celebration.title}
            />
            <CalWarning />
        </TweakProvider>
    );
};
